import React, { ChangeEvent, KeyboardEvent, FormEvent } from "react";
import { Link, RouteComponentProps, Switch, Route, LinkProps } from "react-router-dom";
import Media from "react-media";
import { createPath } from 'history';
import { DateTime } from "luxon";

import HasRight from "../HasRight";
import EventStream from "../EventStream";
import DeletionConfirmationDialog from "../DeletionConfirmationDialog";

import { V2 } from "../../Api";
import { formatDateLocalScale, formatDateTimeLocal, formatName, formatPhone, formatTelephonePartial, formatTimeLocal, formatTimeLocalSmall } from "../../Utilities";

import withContentStyle from "../withContentStyle";
import withTitle from "../withTitle";
import ViewToolbar from "../ViewToolbar";

import "../../input.css";
import "../../messaging.css";
import OverflowMenuItem from "../OverflowMenuItem";
import ToolbarMenuItem from "../ToolbarMenuItem";
import withProps from "../withProps";


const PAGE_SIZE = 50;
const ADDITIONAL_PAGE_SIZE = 25;

// Adapted from https://stackoverflow.com/a/51580782
const BufferLink = ({ to, replace, ...props }: LinkProps) => (
    // NOTE: Using 'to as any' here is wrong, but we don't use the function form of to so it shouldn't be a problem
    <Route path={typeof to === 'string' ? to : createPath(to as any)} exact>
        {({ match }) => (
            <Link {...props} to={to} replace={replace || !!match} />
        )}
    </Route>
);

interface IMessagingMessagesViewState {
    isThreadsLoading: boolean | string;
    isMoreThreadsLoading: boolean | string;
    isMessagesLoading: boolean | string;
    isNumbersLoading: boolean | string;
    isSendingMessage: boolean | string;
    isNumberDropdownOpen: boolean;
    isNotificationsEnabled: boolean;
    isDeleteDialogOpen: boolean;

    deleteMessageId?: string;

    threads: any[];
    messages: any[];
    numbers: any[];
    currentNumber: any;
    thread: any;

    searchText: string;

    messageBody: string;
    messageLengthMax: number;

    newNumber: string;
    totalVisibleMessageCount: number;
}

interface IMessagingMessagesViewProps extends RouteComponentProps<any> {
    showDetailOnly?: boolean;
}

class MessagingMessagesView extends React.Component<IMessagingMessagesViewProps, IMessagingMessagesViewState> {
    private _inputTimer: number | undefined;
    private _timeUpdateTime: number | undefined;
    private _messageScrollRef: React.RefObject<HTMLDivElement> = React.createRef();

    constructor(props: Readonly<IMessagingMessagesViewProps>) {
        super(props);

        const state: IMessagingMessagesViewState = {
            isMessagesLoading: false,
            isMoreThreadsLoading: false,
            isThreadsLoading: true,
            isNumbersLoading: true,
            isSendingMessage: false,
            isNumberDropdownOpen: false,
            isNotificationsEnabled: window.Notification?.permission === "granted",
            isDeleteDialogOpen: false,

            threads: [],
            messages: [],
            numbers: [],

            currentNumber: { number: "" },
            thread: {},

            searchText: "",

            messageBody: "",
            messageLengthMax: 160,

            newNumber: "",
            totalVisibleMessageCount: 50,
        };

        this.state = state;
        this.loadMessages = this.loadMessages.bind(this);
        this.formInputChanged = this.formInputChanged.bind(this);
        this.handleInputChangeThread = this.handleInputChangeThread.bind(this);
        this.renderMaster = this.renderMaster.bind(this);
        this.renderDetail = this.renderDetail.bind(this);
        this.sendMessage = this.sendMessage.bind(this);
        this.handleInputKeydown = this.handleInputKeydown.bind(this);
        this.handleNewMessage = this.handleNewMessage.bind(this);
        this.handleMessageUpdate = this.handleMessageUpdate.bind(this);
        this.handleThreadUpdate = this.handleThreadUpdate.bind(this);
        this.scrollMessages = this.scrollMessages.bind(this);
        this.toggleStatusDisplay = this.toggleStatusDisplay.bind(this);
        this.toggleNumberDropdown = this.toggleNumberDropdown.bind(this);
        this.selectNumber = this.selectNumber.bind(this);
        this.startNewConversation = this.startNewConversation.bind(this);
        this.enableNotifications = this.enableNotifications.bind(this);
        this.goToThread = this.goToThread.bind(this);
        this.toggleDeleteDialog = this.toggleDeleteDialog.bind(this);
        this.realDeleteReminder = this.realDeleteReminder.bind(this);
        this.enableReminders = this.enableReminders.bind(this);
        this.disableReminders = this.disableReminders.bind(this);
        this.loadMoreThreads = this.loadMoreThreads.bind(this);
    }

    public componentDidMount() {
        this.loadNumbers().then(() => {
            this.loadThreads();
        });
        if (this.props.match.params.threadId) {
            this.loadMessages();
        }

        // Bump threads to update listed relative times
        this._timeUpdateTime = window.setInterval(() => this.setState(s => ({ threads: s.threads })), 60000);
    }

    public componentDidUpdate(prevProps: Readonly<IMessagingMessagesViewProps>) {
        if (this.props.match.params.threadId !== prevProps.match.params.threadId) {
            if (this.props.match.params.threadId) {
                this.loadMessages();

                this.setState({
                    messageBody: "",
                    newNumber: "",
                });
            }
            else {
                this.setState({
                    thread: {},
                    messages: [],
                    messageBody: "",
                    newNumber: "",
                });
            }
        }
    }

    public componentWillUnmount() {
        if (this._inputTimer) {
            window.clearTimeout(this._inputTimer);
        }
        if (this._timeUpdateTime) {
            window.clearInterval(this._timeUpdateTime);
        }
    }

    public render() {
        return (
            <div className="messaging-root">
                {this.props.showDetailOnly
                    ? <Switch>
                        <Route exact={true} path="/reminders/messages/new">
                            {this.renderNewMessage()}
                        </Route>
                        <Route>
                            {this.renderDetail(true)}
                        </Route>
                    </Switch>
                    : <Media query="only screen and (min-width: 800px)">
                        {matches => matches ?
                            <>
                                {this.renderMaster()}
                                <Switch>
                                    <Route exact={true} path="/reminders/messages/new">
                                        {this.renderNewMessage()}
                                    </Route>
                                    <Route>
                                        {this.renderDetail(true)}
                                    </Route>
                                </Switch>
                            </>
                            : <Switch>
                                <Route exact={true} path="/reminders/messages">
                                    {this.renderMaster()}
                                </Route>
                                <Route exact={true} path="/reminders/messages/new">
                                    {this.renderNewMessage()}
                                </Route>
                                <Route exact={true} path="/reminders/messages/:id">
                                    {this.renderDetail(false)}
                                </Route>
                            </Switch>
                        }
                    </Media>}
            </div>
        );
    }

    private formInputChanged(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        if (name === "messageBody") {
            // Check if we've used any UCS-2 characters
            let usesUCS2 = false;
            for (const char of value) {
                if (char.codePointAt(0)! > 127) {
                    usesUCS2 = true;
                    break;
                }
            }

            this.setState({
                messageLengthMax: usesUCS2 ? 70 : 160
            });
        }

        this.setState({
            [name]: value
        } as any);
    }

    private handleInputChangeThread(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            [name]: value
        } as any);

        if (this._inputTimer) {
            window.clearTimeout(this._inputTimer);
        }

        this._inputTimer = window.setTimeout(() => this.loadThreads(), 300);
    }

    private async loadThreads() {
        if (!this.state.currentNumber?.number) {
            this.setState({
                isThreadsLoading: "No numbers are available"
            });
            return;
        }

        try {
            this.setState({
                isThreadsLoading: true
            });

            const parameters = [
                `local=${encodeURIComponent(this.state.currentNumber.number)}`,
                `limit=${PAGE_SIZE}`
            ];

            if (this.state.searchText) {
                parameters.push(`q=${encodeURIComponent(this.state.searchText)}`);
            }

            const response = await V2.get<any>("messaging/threads" + (parameters.length > 0 ? ("?" + parameters.join("&")) : ""));

            this.setState({
                isThreadsLoading: false,
                totalVisibleMessageCount: 50,
                threads: response.threads
            });
        }
        catch (e) {
            this.setState({
                isThreadsLoading: e.message || "Could not load threads"
            });
        }
    }

    private async loadMessages() {
        if (this.props.match.params?.id === "new") {
            return;
        }

        try {
            this.setState({
                isMessagesLoading: true
            });

            // Get messages and mark thread as read
            const [messageResponse, thread] = await Promise.all([
                V2.get<any>(`messaging/threads/${this.props.match.params.threadId}/messages`),
                V2.get<any>(`messaging/threads/${this.props.match.params.threadId}`),
                V2.post<void>(`messaging/threads/${this.props.match.params.threadId}/mark-read`)
            ]);

            this.setState({
                isMessagesLoading: false,
                messages: messageResponse.messages.reverse(),
                thread: thread
            });

            this.scrollMessages();
        }
        catch (e) {
            this.setState({
                isMessagesLoading: e.message || "Could not load messages"
            });
        }
    }

    private async loadNumbers() {
        try {
            const result = await V2.get<any>("messaging/numbers/own");

            const sortedNumbers = result.numbers.sort((a: any, b: any) => a.name.localeCompare(b.name));

            this.setState({
                isNumbersLoading: false,
                currentNumber: result.defaultNumber || { number: "" },
                numbers: sortedNumbers,
            });
        }
        catch (e) {
            this.setState({
                isNumbersLoading: e.message || "Could not load numbers"
            });
        }
    }

    private renderMaster() {
        const eventStreams = [
            { id: `message.thread.update.${this.state.currentNumber.number}`, handler: this.handleThreadUpdate }
        ];
        if (!this.state.currentNumber.number) {
            eventStreams.splice(0);
        }

        return <div className="message-master-root">
            <EventStream channels={eventStreams} />
            {!this.props.match.params.threadId
                ? <ViewToolbar>
                    {window.Notification && window.Notification.permission !== "granted"
                        ? <button type="button" className="icon-bell-slash-o" title="Enable notifications..." onClick={this.enableNotifications} />
                        : null}
                    <HasRight right="msg:thread:new">
                        <Link to="/reminders/messages/new" className="icon-plus" title="New Conversation..." />
                    </HasRight>
                </ViewToolbar>
                : null}
            <header style={{ borderRightWidth: !this.props.match.params.threadId ? "1px" : undefined }}>
                <input type="search" placeholder="Search..." name="searchText" value={this.state.searchText} onChange={this.handleInputChangeThread} autoComplete="off" />
            </header>
            <div className="messaging-number-header">
                <button type="button" className="bare" onClick={this.toggleNumberDropdown}>
                    {
                        this.state.currentNumber.number
                            ? <><b>{this.state.currentNumber.name}</b> &mdash; <small className="nowrap"><i>{formatPhone(this.state.currentNumber.number)}</i></small> <i className={`icon-chevron-${this.state.isNumberDropdownOpen ? "up" : "down"}`} /></>
                            : <span className="spinner lds-dual-ring-small" />
                    }
                </button>
            </div>
            {
                this.state.isNumberDropdownOpen
                    ? <ul className="messaging-number-list">
                        {this.state.numbers.map(a =>
                            <li key={a.id} className={a.id === this.state.currentNumber.id ? "number-selected" : undefined}>
                                <button type="button" className="bare" onClick={() => this.selectNumber(a.id)}>
                                    <b>{a.name}</b> &mdash; <small className="nowrap"><i>{formatPhone(a.number)}</i></small>
                                </button>
                            </li>
                        )}
                    </ul>
                    : <div className="message-thread-list-container">
                        <div className="message-thread-list">
                            {this.state.isThreadsLoading === true
                                ? <div className="spinner spinner-center lds-dual-ring" />
                                : typeof this.state.isThreadsLoading === "string"
                                    ? <div className="error-text">{this.state.isThreadsLoading}</div>
                                    : this.state.threads.length === 0
                                        ? <div className="text-light message-info">No conversations</div>
                                        : <>
                                            {this.state.threads.map(a => <BufferLink key={a.id} to={`/reminders/messages/${a.id}`} className={`${a.hasUnreadMessages ? "message-unread" : ""} ${a.id === this.props.match.params.threadId ? "message-thread-active" : ""}`}>
                                                <div className="message-thread-content">
                                                    <div>{a.displayName}</div>
                                                    <div className={"message-thread-preview" + (a.lastMessage?.status === 3 && a.lastMessage?.rejection?.rejectionCode !== 9902 ? " failed" : "")}>{a.lastMessage?.status === 3 && a.lastMessage?.rejection?.rejectionCode !== 9902 ? <i className="icon-times-circle" /> : this.directionToIcon(a.lastMessage?.direction)} {a.lastMessage?.body}</div>
                                                </div>
                                                <div className="message-thread-meta">
                                                    <div title={formatDateTimeLocal(a.lastMessage?.creationTimestamp)}>{formatDateLocalScale(a.lastMessage?.creationTimestamp, true)}</div>
                                                </div>
                                            </BufferLink>)}
                                            <div className="message-info message-hover" onClick={this.loadMoreThreads}>
                                                {this.state.isMoreThreadsLoading === true
                                                    ? <>Loading messages&nbsp;<span className="spinner lds-dual-ring" /></>
                                                    : typeof this.state.isMoreThreadsLoading === "string"
                                                        ? <span className="error-text">{this.state.isMoreThreadsLoading}</span>
                                                        : "Click to load more"}
                                            </div>
                                        </>}
                        </div>
                    </div>
            }
        </div>;
    }

    private renderDetail(handleEnter = false) {
        const msgMax = this.state.messageLengthMax;
        const msgCount = Math.ceil(this.state.messageBody.length / msgMax);
        const msgCharsLeft = Math.floor(this.state.messageBody.length % msgMax);

        const eventStreams = [
            { id: `message.new.${this.props.match.params.threadId}`, handler: this.handleNewMessage },
            { id: `message.change.${this.props.match.params.threadId}`, handler: this.handleMessageUpdate },
        ];

        return <div className="message-detail-root">
            {!!this.props.match.params.threadId
                ? <>
                    <DeletionConfirmationDialog
                        isOpen={this.state.isDeleteDialogOpen}
                        description={null}
                        onRequestClose={this.toggleDeleteDialog}
                        onSuccess={this.loadMessages}
                        onConfirm={this.realDeleteReminder}>
                        <p>
                            Are you sure you want to delete this reminder?
                        </p>
                    </DeletionConfirmationDialog>
                    <EventStream channels={eventStreams} />
                    <header>
                        <span>{this.state.thread.displayName}&nbsp;&nbsp;{this.state.thread.areRemindersDisabled ? <i className="icon-calendar-times-o" style={{ color: "firebrick" }} title="Reminders disabled" /> : null}</span>
                        <div className="message-header-buttons">
                            {!this.props.showDetailOnly
                                ? <>
                                    <OverflowMenuItem>
                                        <ToolbarMenuItem>
                                            {this.state.thread.areRemindersDisabled
                                                ? <button type="button" className="text-with-icon" onClick={this.enableReminders}><i className="icon-bell" /><span>Enable reminders</span></button>
                                                : <button type="button" className="text-with-icon" onClick={this.disableReminders}><i className="icon-bell-slash" /><span>Disable reminders</span></button>}
                                        </ToolbarMenuItem>
                                        {this.state.thread.links?.map((a: any) =>
                                            <ToolbarMenuItem key={a.targetId}>
                                                {this.threadLinkToElement(a)}
                                            </ToolbarMenuItem>
                                        )}
                                    </OverflowMenuItem>
                                    <Link to="/reminders/messages" className="message-header-button message-header-spacer icon-times" title="Close conversation" />
                                </>
                                : <button type="button" className="message-header-button message-header-spacer icon-times" title="Back to outreach" onClick={() => this.props.history.goBack()} />}
                        </div>
                    </header>
                    <div className="message-details-inner">
                        <div className="message-details-messages" ref={this._messageScrollRef}>
                            {this.state.isMessagesLoading
                                ? <div className="spinner spinner-center lds-dual-ring" />
                                : this.renderMessages()}
                        </div>
                        {typeof this.state.isSendingMessage === "string"
                            ? <div className="message-details-error">{this.state.isSendingMessage}</div>
                            : null}
                        <HasRight right="msg:send">
                            <div className="message-details-input-root">
                                <textarea name="messageBody" value={this.state.messageBody} onChange={this.formInputChanged} onKeyDown={handleEnter ? this.handleInputKeydown : undefined} disabled={this.state.isSendingMessage === true} />
                                <button type="button" className="message-send-button" title="Send SMS..." onClick={this.sendMessage} disabled={!this.state.messageBody || this.state.isSendingMessage === true}>
                                    <i className="icon-reply" />
                                    <div>SMS</div>
                                </button>
                            </div>
                            <div className="message-details-input-sub">
                                {handleEnter ? <span>Pressing Enter sends your message. Press Shift+Enter to add a new line.</span> : undefined}
                                <span className="spring" />
                                <span className="message-details-input-counter">{msgMax - msgCharsLeft}{msgCount > 1 ? ` (${msgCount})` : ""}</span>
                            </div>
                        </HasRight>
                    </div>
                </>
                : <div className="messages-no-conversation">
                    <div className="text-light">No conversation selected</div>
                </div>
            }
        </div>;
    }

    private renderNewMessage() {
        return <div className="message-detail-root">
            <header>
                <form className="input-compound" onSubmit={this.startNewConversation}>
                    <span className="prefix">+1</span>
                    <input type="tel" name="newNumber" maxLength={14} value={formatTelephonePartial(this.state.newNumber)} onChange={this.formInputChanged} autoFocus={true} autoComplete="off" />
                    <button type="submit" className="bare icon-arrow-right" disabled={this.state.newNumber?.length < 14} />
                </form>
                <Link to="/reminders/messages" className="message-header-button icon-times" replace={true} title="Close conversation" />
            </header>
            <div className="messages-no-conversation">
                <div>Enter a number above to start a conversation</div>
            </div>
        </div>;
    }

    private renderMessages() {
        let lastTimeDivider;
        const output = [];

        for (const message of this.state.messages) {
            const timestamp = DateTime.fromISO(message.creationTimestamp);
            if (!lastTimeDivider || timestamp.diff(lastTimeDivider, "minutes").minutes >= 30) {
                // Insert divider
                output.push(<div key={message.id + "_div"} className="message-divider">
                    <div>{formatDateTimeLocal(timestamp)}</div>
                </div>);

                lastTimeDivider = timestamp;
            }

            output.push(<div key={message.id} className={`message-wrapper ${message.direction === 0 ? "received" : ((message.sendAfter && message.status === 0) ? "sent queued" : "sent")}`} onClick={() => this.toggleStatusDisplay(message)}>
                <div className="message-content">
                    <div className="message-bubble-outer">
                        <HasRight right="remind:send">
                            {message.status === 0 && !message.sender?.id
                                ? <div className="message-button-extra">
                                    <button className="bare icon-trash" title="Delete reminder" onClick={() => this.deleteReminder(message.id)} />
                                </div>
                                : null}
                        </HasRight>
                        <div className="message-bubble" title={formatDateTimeLocal(timestamp)}>{this.textToElements(message.body)}</div>
                    </div>
                    {this.messageStatusToDisplay(message)}
                </div>
            </div>);
        }

        return output;
    }

    private async sendMessage() {
        if (!this.props.match.params.threadId) {
            return;
        }

        try {
            this.setState({
                isSendingMessage: true,
            });

            const data = {
                body: this.state.messageBody
            };

            await V2.post<void>(`messaging/threads/${this.props.match.params.threadId}/messages`, data);

            this.setState({
                isSendingMessage: false,
                messageBody: "",
            });
        }
        catch (e) {
            this.setState({
                isSendingMessage: e.message || "Could not send your message",
            });
        }
    }

    private handleInputKeydown(e: KeyboardEvent<HTMLTextAreaElement>) {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            this.sendMessage();
            return;
        }
    }

    private handleNewMessage(event: MessageEvent<string>) {
        const data = JSON.parse(event.data);

        if (!this.props.match.params.threadId || data.threadId !== this.props.match.params.threadId) {
            return;
        }

        const messages = this.state.messages;

        messages.push(data.message);

        this.setState({
            messages
        });

        this.scrollMessages();

        V2.post<void>(`messaging/threads/${this.props.match.params.threadId}/mark-read`).catch(e => { });
    }

    private handleMessageUpdate(event: MessageEvent<string>) {
        const data = JSON.parse(event.data);

        if (!data.messageId) {
            return;
        }

        const messages = this.state.messages;
        const message = messages.find((a: any) => a.id === data.messageId);
        if (!message) {
            return;
        }

        message.status = data.status;
        if (data.rejection) {
            message.rejection = data.rejection;
        }

        this.setState({
            messages
        });
    }

    private handleThreadUpdate(event: MessageEvent<string>) {
        const data = JSON.parse(event.data);

        if (!data.threadId) {
            return;
        }

        const threads = this.state.threads;

        // If the thread referenced is in our list, move it up
        const thread = threads.find((a: any) => a.id === data.threadId);
        if (thread) {
            // Move thread up
            thread.hasUnreadMessages = data.hasUnreadMessages;
            if (data.lastMessage) {
                thread.lastMessage = data.lastMessage;

                threads.sort((a: any, b: any) => (b.lastMessage?.creationTimestamp || "").localeCompare(a.lastMessage?.creationTimestamp || ""));
            }
        }
        else {
            // Add new entry
            const newThread = {
                id: data.threadId,
                displayName: data.displayName,
                hasUnreadMessages: data.hasUnreadMessages,
                lastMessage: data.lastMessage
            };

            threads.unshift(newThread);

            if (threads.length > this.state.totalVisibleMessageCount) {
                threads.splice(this.state.totalVisibleMessageCount);
            }
        }

        if (this.state.isNotificationsEnabled && data.lastMessage?.direction === 0) {
            // Notify on incoming messages only
            var notification = new Notification(`SMS from ${data.displayName}`, {
                body: data.lastMessage?.body || "New Message"
            });
            notification.onclick = () => this.goToThread(data.threadId);
        }

        this.setState({
            threads
        });
    }

    private scrollMessages() {
        if (this._messageScrollRef.current) {
            this._messageScrollRef.current.scrollTop = this._messageScrollRef.current.scrollHeight;
        }
    }

    private messageStatusToDisplay(message: any) {
        let statusContent;
        let forceShowStatus = false;
        switch (message.status) {
            case 0:
                statusContent = <div className="message-status status-provisional">{message.sendAfter ? `Waiting to send after ${formatTimeLocal(message.sendAfter)}` : "Queued"}</div>;
                forceShowStatus = true;
                break;
            case 1:
                statusContent = <div className="message-status status-provisional">Sending...</div>;
                forceShowStatus = true;
                break;
            case 2:
            case 10:
            case 11:
                statusContent = message.sender?.id
                    ? <div className="message-status">{formatName(message.sender.name)} - {formatTimeLocalSmall(message.creationTimestamp)}</div>
                    : <div className="message-status">{formatTimeLocalSmall(message.creationTimestamp)}</div>;
                break;
            case 3:
                if (message.rejection) {
                    if (message.rejection.rejectionCode === 9902) {
                        statusContent = <div className="message-status">Delivery unknown: {this.rejectionToDescription(message.rejection.rejectionCode)}</div>;
                    }
                    else {
                        statusContent = <div className="message-status status-failed">Failed to send. {this.rejectionToDescription(message.rejection.rejectionCode)}</div>;
                    }
                }
                else {
                    statusContent = <div className="message-status status-failed">Failed to send</div>;
                }
                forceShowStatus = true;
                break;

            default: return null;
        }

        if (forceShowStatus || message.showStatus) {
            return statusContent;
        }

        return null;
    }

    private rejectionToDescription(code: number) {
        switch (code) {
            case 4001: return "Service is not allowed";
            case 4301: return "Malformed message encoding";
            case 4302: return "Invalid from number";
            case 4303: return "Invalid to number";
            case 4350: return "Send validation failed. Number may be invalid";
            case 4360: return "Message expired";
            case 4401: return "Upstream provider could not route the message";
            case 4403: return "Sending is not allowed on this number";
            case 4404: return "Sending to this number is not allowed; may not support SMS messages";
            case 4405: return "From number does not exist";
            case 4406: return "Destination number is not assigned to a carrier";
            case 4407: return "From number is not assigned to this account";
            case 4408: return "Destination number is not assigned to an account";
            case 4409: return "Invalid destination profile. Destination likely doesn't support MMS messages";
            case 4410: return "Could not retrieve attached media";
            case 4411: return "Media size is too large";
            case 4412: return "Type of attached media is not supported";
            case 4420: return "No route is available to destination carrier";
            case 4421: return "No route is available to destination carrier";
            case 4431: return "Sending messages from short code is not allowed";
            case 4432: return "Sending messages to this country is not allowed";
            case 4433: return "Toll-free messaging is not enabled on this number";
            case 4434: return "Sending messages to this toll-free number is not allowed";
            case 4435: return "Too many recipients";
            case 4451: return "Invalid user ID";
            case 4452: return "Invalid application ID";
            case 4470: return "Detected as potential spam";
            case 4481: return "From number is blacklisted";
            case 4482: return "Destination number is blocked from receiving messages";
            case 4492: return "You cannot send messages to emergency numbers";
            case 4493: return "Unauthorized for sending messages";

            case 4700: return "Destination does not support SMS messages";
            case 4701: return "Destination is unreachable";
            case 4702: return "Destination is currently unavailable";
            case 4720: return "Invalid destination phone number";
            case 4721: return "Destination phone number is deactivated";
            case 4730: return "No route to carrier exists";
            case 4740: return "Invalid source number";
            case 4750: return "Carrier rejected the message";
            case 4751: return "Message is too long for destination carrier";
            case 4752: return "Carrier rejected the message for being too long or malformed";
            case 4770: return "Carrier rejected the message as spam";
            case 4775: return "Carrier rejected the message because the subscriber opted out";
            case 4780: return "Carrier rejected the message due to too many messages sent";
            // 4790-4793 are shortcode errors

            // Provider-specific errors
            case 5100:
            case 5101: return `Upstream provider error (${code})`;
            case 5106: return "Upstream provider could not route the message";
            case 5111:
            case 5201:
            case 5211:
            case 5220: return `Upstream provider error (${code})`;
            case 5231: return "Upstream provider did not receive all parts of message";
            case 5500: return "General service failure";
            case 5501: return "No destination is available to upstream provider";
            case 5999: return "Unknown error from upstream provider";

            case 5600: return "Carrier service is unavailable";
            case 5610: return "Carrier service failure";
            case 5620: return "Carrier service application error";
            case 5630: return "Carrier did not ackowledge message";
            case 5650: return "Carrier could not send message";

            case 9902: return "Delivery receipt timeout expired";
            case 9999: return "Unknown error";

            default: return `Other error code ${code}`;
        }
    }

    private toggleStatusDisplay(message: any) {
        const messages = this.state.messages;

        message.showStatus = !message.showStatus;

        this.setState({
            messages
        });
    }

    private textToElements(text: string) {
        const parts = text.replace(/\r/g, "").split("\n");
        const output = [];


        for (let i = 0; i < parts.length; i++) {
            if (i > 0) {
                output.push(<br key={i} />);
            }
            output.push(parts[i]);
        }

        return output;
    }

    private toggleNumberDropdown() {
        const isNumberDropdownOpen = this.state.isNumberDropdownOpen;

        this.setState({
            isNumberDropdownOpen: !isNumberDropdownOpen
        });
    }

    private selectNumber(id: string) {
        const numbers = this.state.numbers;
        const currentNumber = numbers.find(a => a.id === id);
        if (currentNumber === null) {
            return;
        }

        this.props.history.push("/reminders/messages");

        this.setState({
            currentNumber,
            isNumberDropdownOpen: false,
        }, () => this.loadThreads());
    }

    private async startNewConversation(e: FormEvent) {
        e.preventDefault();

        try {
            const newNumber = this.state.newNumber.replace(/[^0-9]/g, "");
            if (newNumber.length < 10) {
                return;
            }

            const result = await V2.get<any>(`messaging/threads?local=${encodeURIComponent(this.state.currentNumber.number)}&q=${encodeURIComponent(newNumber)}`);

            if (result.threads && result.threads.length > 0) {
                // Found an existing thread; go to it
                const thread = result.threads[0];
                this.props.history.replace(`/reminders/messages/${thread.id}`);

                return;
            }
            else {
                // Create a new thread
                const data = {
                    localNumber: this.state.currentNumber.number,
                    remoteNumber: `+1${newNumber}`
                };

                const result = await V2.post<any>("messaging/threads", data);

                this.props.history.replace(`/reminders/messages/${result.id}`);
            }
        }
        catch (e) {
            // TODO: Determine what to do with this error
        }
    }

    private directionToIcon(direction: number) {
        switch (direction) {
            case 0: return null;
            case 1: return <i className="icon-mail-reply" />;
            default: return null;
        }
    }

    private async enableNotifications() {
        const permission = await Notification.requestPermission();
        if (permission === "granted") {
            // Enable notifications
            this.setState({
                isNotificationsEnabled: true
            });
        }
    }

    private goToThread(id: string) {
        if (this.props.location.pathname !== `/reminders/messages/${id}`) {
            this.props.history.push(`/reminders/messages/${id}`);
        }
    }

    private deleteReminder(messageId: string) {
        this.setState({
            isDeleteDialogOpen: true,
            deleteMessageId: messageId,
        });
    }

    private toggleDeleteDialog() {
        const isDeleteDialogOpen = this.state.isDeleteDialogOpen;
        this.setState({
            isDeleteDialogOpen: !isDeleteDialogOpen
        });
    }

    private async realDeleteReminder() {
        if (!this.state.deleteMessageId) {
            return;
        }

        const reminderInfo = await V2.get<any>(`messaging/reminders?message_id=${this.state.deleteMessageId}&limit=1`);
        if (!reminderInfo.reminders?.length) {
            return;
        }

        const data = {
            status: "deleted"
        };

        await V2.patch(`messaging/reminders/${reminderInfo.reminders[0].id}`, data);

        this.setState({
            deleteMessageId: "",
        });
    }

    private async enableReminders() {
        try {
            const thread = this.state.thread;

            await V2.delete(`messaging/blacklist/${encodeURIComponent(this.state.thread.remoteNumber)}`);

            thread.areRemindersDisabled = false;

            this.setState({
                thread
            });
        }
        catch (e) {
            // Not sure what to do with an error here
        }
    }

    private async disableReminders() {
        try {
            const thread = this.state.thread;

            await V2.put(`messaging/blacklist/${encodeURIComponent(this.state.thread.remoteNumber)}`);

            thread.areRemindersDisabled = true;

            this.setState({
                thread
            });
        }
        catch (e) {
            // Not sure what to do with an error here
        }
    }

    private threadLinkToElement(link: any) {
        switch (link.type) {
            case "outreach-ahs":
                return <Link to={`/appointmentspecialist/pastcontact/survey/${link.targetId}`}><i className="icon-mail-forward" /><span>Go to outreach</span></Link>
            default:
                return null;
        }
    }

    private async loadMoreThreads() {
        if (this.state.isMoreThreadsLoading === true) {
            return;
        }

        if (!this.state.currentNumber?.number) {
            this.setState({
                isThreadsLoading: "No numbers are available"
            });
            return;
        }

        try {
            this.setState({
                isMoreThreadsLoading: true
            });

            const { currentNumber, searchText, threads, totalVisibleMessageCount } = this.state;

            const parameters = [
                `local=${encodeURIComponent(currentNumber.number)}`,
                `offset=${threads.length}`,
                `limit=${ADDITIONAL_PAGE_SIZE}`
            ];

            if (this.state.searchText) {
                parameters.push(`q=${encodeURIComponent(searchText)}`);
            }

            const response = await V2.get<any>("messaging/threads" + (parameters.length > 0 ? ("?" + parameters.join("&")) : ""));
            Array.prototype.push.apply(threads, response.threads);

            this.setState({
                isMoreThreadsLoading: false,
                totalVisibleMessageCount: totalVisibleMessageCount + ADDITIONAL_PAGE_SIZE,
                threads
            });
        }
        catch (e) {
            this.setState({
                isMoreThreadsLoading: e.message || "Could not load threads"
            });
        }
    }
}

export default withTitle("Messages", withContentStyle("noborder", MessagingMessagesView));
export const MessagingMessagesViewDetailOnly = withContentStyle("noborder", withProps(MessagingMessagesView, { showDetailOnly: true }));
